import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/ProductCard';
import { FiMinus, FiPlus, FiShoppingBag } from 'react-icons/fi';
import './ProductDetailPage.css';

const ProductDetailPage = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const [pRes, rRes] = await Promise.all([
          api.get(`/products/${id}`),
          api.get(`/products/${id}/related`),
        ]);
        setProduct(pRes.data.product);
        setRelated(rRes.data.products);
        setActiveImg(0);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = async () => {
    setAdding(true);
    await addToCart(product._id, qty);
    setAdding(false);
  };

  if (loading) return <div className="loading-spinner" style={{paddingTop:'160px'}}><div className="spinner" /></div>;
  if (!product) return <div className="empty-state" style={{paddingTop:'160px'}}><h3>Product not found</h3></div>;

  const displayPrice = product.salePrice || product.price;
  const isOutOfStock = product.stock === 0;
  const discount = product.isOnSale ? Math.round(((product.price - product.salePrice) / product.price) * 100) : 0;

  return (
    <div className="product-detail">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Home</Link> / <Link to="/shop">Shop</Link> / <Link to={`/shop?category=${product.category}`}>{product.category}</Link> / {product.name}
        </div>

        <div className="product-detail__grid">
          {/* Images */}
          <div className="product-images">
            <div className="product-images__main">
              <img src={product.images[activeImg] || 'https://via.placeholder.com/600x800'} alt={product.name} />
              {discount > 0 && <div className="product-images__discount">-{discount}%</div>}
            </div>
            {product.images.length > 1 && (
              <div className="product-images__thumbs">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    className={`thumb ${activeImg === i ? 'active' : ''}`}
                    onClick={() => setActiveImg(i)}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="product-info">
            <p className="product-category">{product.category}</p>
            <h1 className="product-name">{product.name}</h1>
            <div className="product-price">
              <span className="price-main">₹{displayPrice.toFixed(0)}</span>
              {product.isOnSale && (
                <>
                  <span className="price-original">₹{product.price.toFixed(0)}</span>
                  <span className="price-save">{discount}% off</span>
                </>
              )}
            </div>

            {!isOutOfStock ? (
              <>
                <div className="product-qty">
                  <button onClick={() => setQty(q => Math.max(1, q - 1))}><FiMinus /></button>
                  <span>{qty}</span>
                  <button onClick={() => setQty(q => Math.min(product.stock, q + 1))}><FiPlus /></button>
                </div>
                <p className="product-stock-info">{product.stock} items in stock</p>
                <button
                  className="btn btn-primary btn-full btn-lg"
                  onClick={handleAddToCart}
                  disabled={adding}
                >
                  <FiShoppingBag /> {adding ? 'Adding...' : 'Add to Cart'}
                </button>
                <Link to="/cart" className="btn btn-outline btn-full" style={{marginTop:'12px'}}>
                  View Cart
                </Link>
              </>
            ) : (
              <div className="out-of-stock-msg">
                <span className="badge badge-out">Out of Stock</span>
                <p>This product is currently unavailable.</p>
              </div>
            )}

            <hr className="divider" />
            <div className="product-desc">
              <h3>Product Details</h3>
              <p>{product.description}</p>
            </div>
            <div className="product-assurance">
              <div>🚚 Free shipping above ₹499</div>
              <div>↩️ 7-day easy returns</div>
              <div>🔒 Secure checkout via Razorpay</div>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="related-products">
            <h2>You May Also Like</h2>
            <div className="gold-line" style={{margin:'12px 0 40px'}} />
            <div className="grid-4">
              {related.map(p => <ProductCard key={p._id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
